/**
 * 前端错误监控 SDK - 传输层
 * 负责队列管理、批量发送、背压保护、session 内去重
 */
import type { ErrorTrackerConfig, FrontendErrorEvent } from './types';

/** session 内同类错误最大上报次数 */
const MAX_SAME_ERROR_PER_SESSION = 5;
/** 内存队列最大长度 */
const MAX_QUEUE_SIZE = 100;

let config: ErrorTrackerConfig | null = null;
let flushTimer: ReturnType<typeof setInterval> | null = null;

/** 内存队列 */
const queue: FrontendErrorEvent[] = [];

/**
 * session 内同类错误计数
 * key = fingerprintHint 或 `${name}:${message}` 的前 120 字符
 */
const dedupeMap = new Map<string, number>();

function getDedupeKey(event: FrontendErrorEvent): string {
  if (event.fingerprintHint) return event.fingerprintHint;
  return `${event.name ?? ''}:${event.message}`.slice(0, 120);
}

/** 检查并更新 session 内去重计数，返回 true 表示应丢弃 */
function shouldDedupe(event: FrontendErrorEvent): boolean {
  const key = getDedupeKey(event);
  const count = dedupeMap.get(key) ?? 0;
  if (count >= MAX_SAME_ERROR_PER_SESSION) return true;
  dedupeMap.set(key, count + 1);
  return false;
}

/** 将事件推入队列（含背压保护） */
export function enqueue(event: FrontendErrorEvent): void {
  if (!config?.enabled) return;

  // ① session 内同类去重
  if (shouldDedupe(event)) return;

  // ② sampleRate 采样（仅随机丢弃，不计入去重计数）
  const sampleRate = config.sampleRate ?? 1.0;
  if (sampleRate < 1.0 && Math.random() > sampleRate) return;

  // ③ 内存队列背压：满了丢弃最旧的
  if (queue.length >= MAX_QUEUE_SIZE) {
    queue.shift();
  }
  queue.push(event);

  // ④ 批量触发：达到 batchSize 时提前 flush
  const batchSize = config.flushBatchSize ?? 5;
  if (queue.length >= batchSize) {
    void flush();
  }
}

/** 发送一批事件 */
async function sendBatch(batch: FrontendErrorEvent[]): Promise<void> {
  if (!config?.endpoint || batch.length === 0) return;

  const payload = JSON.stringify({ events: batch });

  // sendBeacon 优先（页面卸载安全，非阻塞）
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' });
    const sent = navigator.sendBeacon(config.endpoint, blob);
    if (sent) return;
  }

  // 回退 fetch（keep-alive 避免页面卸载时中断）
  try {
    await fetch(config.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    });
  } catch {
    // 网络错误静默失败，不影响主业务
  }
}

/** 冲刷当前队列 */
export async function flush(): Promise<void> {
  if (queue.length === 0) return;
  const batchSize = config?.flushBatchSize ?? 5;
  const batch = queue.splice(0, batchSize);
  await sendBatch(batch);
}

/** 初始化传输层 */
export function initTransport(cfg: ErrorTrackerConfig): void {
  config = cfg;

  // 启动定时 flush
  if (flushTimer) clearInterval(flushTimer);
  const interval = cfg.flushInterval ?? 3000;
  flushTimer = setInterval(() => {
    void flush();
  }, interval);

  // 页面卸载时强制 flush
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => void flush(), { once: false });
    window.addEventListener('beforeunload', () => void flush(), { once: false });
  }
}

/** 销毁传输层（测试用） */
export function destroyTransport(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  queue.length = 0;
  dedupeMap.clear();
  config = null;
}
